import PhoneInput from "react-phone-input-2";
import { InputWrapper } from "../Wrapper/InputBase";
import "./css/phoneInput.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { colors } from "common/const/colors";
import { useController, useFormContext } from "react-hook-form";
import { alpha3ToAlpha2 } from "i18n-iso-countries";

export const CustomPhoneInput = ({
  // InputWrapper
  label,
  placeholder,
  caption,
  required,

  // Input
  codeFieldName,
  numberFieldName,
  onChange,
  disabled,
}) => {
  if (!codeFieldName || !numberFieldName) {
    throw new Error(
      "CustomPhoneInput: codeFieldName, numberFieldName is Empty",
    );
  }

  const { control } = useFormContext();

  // 국가 코드 필드
  const codeController = useController({
    name: codeFieldName,
    control,
    rules: {
      validate: (value) => {
        if (required && (!value || String(value).trim() === "")) {
          return false;
        }
        return true;
      },
    },
  });

  // 전화번호 필드
  const numberController = useController({
    name: numberFieldName,
    control,
    rules: {
      validate: (value) => {
        if (required && (!value || String(value).trim() === "")) {
          return false;
        }
        return true;
      },
    },
  });

  const phoneCode = codeController.field.value ?? "";
  const phoneNumber = numberController.field.value ?? "";
  const error =
    numberController.fieldState.error || codeController.fieldState.error;

  const [dropdownStyle, setDropdownStyle] = useState({});
  const wrapperRef = useRef(null);

  const handleOpen = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setDropdownStyle({
        top: rect.bottom + window.scrollY,
        left: rect.left,
        width: rect.width,
        zIndex: 10,
      });
    }
  };

  useEffect(() => {
    const flagButton = wrapperRef.current?.querySelector(".selected-flag");
    if (flagButton) {
      flagButton.addEventListener("click", handleOpen);
      return () => flagButton.removeEventListener("click", handleOpen);
    }
  }, []);

  useEffect(() => {
    const countryList = wrapperRef.current?.querySelector(".country-list");
    if (!countryList) return;

    // MutationObserver로 드롭다운 열림/닫힘 감지
    const observer = new MutationObserver(() => {
      const isOpen = !countryList.classList.contains("hide");

      if (isOpen) {
        // 드롭다운이 열렸을 때
        const rect = wrapperRef.current.getBoundingClientRect();
        countryList.style.position = "fixed";
        countryList.style.top = `${rect.bottom}px`;
        countryList.style.left = `${rect.left}px`;
        countryList.style.width = `${rect.width}px`;
        countryList.style.zIndex = "10";
      }
    });

    observer.observe(countryList, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  // 스크롤 시 위치 업데이트
  useEffect(() => {
    const updatePosition = () => {
      const countryList = wrapperRef.current?.querySelector(".country-list");

      if (countryList && !countryList.classList.contains("hide")) {
        const rect = wrapperRef.current.getBoundingClientRect();
        countryList.style.top = `${rect.bottom}px`;
        countryList.style.left = `${rect.left}px`;
      }
    };

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  const handlePhoneChange = (newValue, data) => {
    const localNumber = newValue.substring(data.dialCode?.length);
    const info = { code: data.dialCode, number: localNumber };

    if (onChange) {
      onChange(info);
    } else {
      // useController의 onChange를 직접 호출 → form state 갱신 + 검증 자동
      codeController.field.onChange(info.code);
      numberController.field.onChange(info.number);
    }
  };

  const handleBlur = () => {
    codeController.field.onBlur();
    numberController.field.onBlur();
  };

  const defaultCountry = useMemo(
    () => alpha3ToAlpha2(localStorage.getItem("country"))?.toLowerCase(),
    [],
  );

  return (
    <InputWrapper
      ref={wrapperRef}
      label={label}
      caption={caption}
      rules={{ required }}
      error={error}>
      <PhoneInput
        key={phoneCode ? "has-value" : "none-value"}
        onBlur={handleBlur}
        onChange={handlePhoneChange}
        inputStyle={{
          borderColor: error ? colors.Red : colors.Gray,
        }}
        dropdownStyle={dropdownStyle}
        disabled={disabled}
        value={String(phoneCode + phoneNumber || "")}
        country={!phoneCode ? defaultCountry : undefined}
        placeholder={placeholder}
      />
    </InputWrapper>
  );
};
